import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useOrder } from "../context/OrderContext";
import api from "../services/apiService";
import Header from "../components/Header.jsx";
import Cart from "../components/Cart.jsx";
import CheckoutModal from "../components/CheckoutModal.jsx";
import CategorySidebar from "../components/CategorySidebar.jsx";
import MenuSection from "../components/MenuSection.jsx";

const MenuPage = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const {
    addItem,
    setRestaurant,
    getTotalItems,
    getTotalPrice,
    clearCart,
    updateQuantity,
    removeItem,
    items: cartItems,
  } = useCart();

  const { createOrder } = useOrder();

  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [restaurantData, setRestaurantData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [addingToCart, setAddingToCart] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const memoizedSetRestaurant = useCallback(
    (restaurant) => {
      setRestaurant(restaurant);
    },
    [setRestaurant]
  );

  // Effect to load restaurant data and menu items from API
  useEffect(() => {
    const loadRestaurant = async () => {
      if (!restaurantId) {
        setError("No restaurant ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch menu items from API with Authorization header
        const token = localStorage.getItem("token");
        const menuRes = await api.get(
          `/menu/public/restaurant/${restaurantId}`
        );

        // Ensure we have an array of menu items
        const menuItems = Array.isArray(menuRes.data) ? menuRes.data : [];

        // Validate menu items structure
        const validatedMenuItems = menuItems.map((item) => ({
          id: item.id,
          name: item.name || "Unnamed Item",
          description: item.description || "",
          type: item.type || "veg", // 'veg' or 'non-veg'
          price: parseFloat(item.price) || 0,
          quantity: item.quantity || "",
          category: item.category || "uncategorized",
          image:
            item.image ||
            "https://via.placeholder.com/400x300/E5E7EB/6B7280?text=No+Image",
        }));

        // Extract unique categories from menu items
        const categories = Array.from(
          new Set(
            validatedMenuItems.map((item) => item.category).filter(Boolean)
          )
        ).sort();

        // If no categories found, add a default one
        if (categories.length === 0) {
          categories.push("All Items");
        }

        // Dummy restaurant info (replace with real API if available)
        const restaurant = {
          id: restaurantId,
          name: `Restaurant #${restaurantId}`,
          description: `Quality food delivered to your door`,
          address: "Address not provided",
          phone: "Phone not provided",
          rating: 4.5,
          reviewCount: 128,
          deliveryTime: "25-35 min",
          deliveryFee: 2.99,
          logo: `https://via.placeholder.com/100x100/4F46E5/FFFFFF?text=R`,
          menu: validatedMenuItems,
          categories,
        };

        setRestaurantData(restaurant);
        memoizedSetRestaurant(restaurant);

        // Only set the initial category once when data is first loaded
        if (categories.length > 0) {
          setSelectedCategory((prevCategory) =>
            prevCategory === null ? categories[0] : prevCategory
          );
        }
      } catch (err) {
        console.error("Error loading restaurant:", err);
        setError(`Failed to load restaurant data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadRestaurant();
  }, [restaurantId]);

  const handleAddToCart = async (item) => {
    try {
      setAddingToCart(item.id);
      await new Promise((resolve) => setTimeout(resolve, 300));
      addItem(item);
    } catch (error) {
      console.error("Error adding item to cart:", error);
    } finally {
      setAddingToCart(null);
    }
  };

  const handleFavoriteToggle = (itemId) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(itemId)) {
        newFavorites.delete(itemId);
      } else {
        newFavorites.add(itemId);
      }
      return newFavorites;
    });
  };

  const handleCheckout = async (orderData) => {
    try {
      if (!cartItems || cartItems.length === 0) {
        alert("Your cart is empty. Please add items before checkout.");
        return;
      }

      // Prepare items for API (menuItemId, quantity, specialInstructions, ...)
      const itemsForApi = orderData.items.map((item) => ({
        menuItemId: item.id,
        quantity: item.quantity,
        name: item.name,
        description: item.description,
        type: item.type,
        price: item.price,
        category: item.category,
        image: item.image,
        specialInstructions: item.specialInstructions || "",
      }));

      const payload = {
        items: itemsForApi,
        status: "pending",
        totalPrice: orderData.total,
        tableNumber: orderData.tableNumber,
        paymentStatus:
          orderData.paymentMethod === "cash" ? "Paid" : orderData.paymentMethod,
        customerName: orderData.customerName,
        customerPhoneNumber: orderData.phone,
        createdAt: new Date().toISOString(),
      };

      // Create order using OrderContext
      const order = await createOrder(restaurantId, payload);

      if (order && order.id) {
        clearCart();
        setShowCheckout(false);
        setShowCart(false);
        navigate(`/order-success/${order.id}`);
      } else {
        throw new Error("Order creation response was invalid");
      }
    } catch (error) {
      console.error("Error during checkout:", error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        alert(
          `Failed to place order: ${
            error.response.data?.message || "Server error"
          }`
        );
      } else if (error.request) {
        // The request was made but no response was received
        alert(
          "No response from server. Please check your internet connection."
        );
      } else {
        // Something happened in setting up the request that triggered an Error
        alert(`Error placing order: ${error.message || "Please try again"}`);
      }
    }
  };

  const handleBackToHome = () => navigate("/");
  const handleRetry = () => window.location.reload();
  const handleCategoryChange = (category) => setSelectedCategory(category);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-primary-200 rounded-full animate-spin mx-auto mb-8">
                <div className="w-4 h-4 bg-primary-600 rounded-full absolute top-0 left-1/2 transform -translate-x-1/2"></div>
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900">
              Loading Menu
            </h3>
            <p className="text-gray-500">Preparing something delicious...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !restaurantData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Oops!</h1>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Restaurant Not Found
          </h2>
          <p className="text-gray-600 mb-8">
            {error ||
              "The restaurant you're looking for doesn't exist or may have been removed."}
          </p>
          <div className="space-y-3">
            <button
              onClick={handleRetry}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Try Again
            </button>
            <button
              onClick={handleBackToHome}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-xl transition-all duration-200"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Filter menu items by selected category (search will be handled in MenuSection)
  let menuItems =
    selectedCategory && restaurantData?.menu
      ? restaurantData.menu.filter((item) => item.category === selectedCategory)
      : restaurantData?.menu || [];

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <Header
          restaurant={restaurantData}
          onCartClick={() => setShowCart(true)}
          cartItems={getTotalItems()}
          onCheckOrderStatus={() =>
            navigate(`/order-status?restaurantId=${restaurantId}`)
          }
        />

        {/* <RestaurantHero restaurant={restaurantData} /> */}

        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Search Bar moved to MenuSection */}
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <div className="lg:w-72 xl:w-80 flex-shrink-0">
              <CategorySidebar
                categories={restaurantData.categories}
                selectedCategory={selectedCategory}
                handleCategoryChange={handleCategoryChange}
                restaurantMenu={restaurantData.menu}
              />
            </div>

            <div className="flex-1">
              <MenuSection
                selectedCategory={selectedCategory}
                menuItems={menuItems}
                categories={restaurantData.categories}
                handleAddToCart={handleAddToCart}
                addingToCart={addingToCart}
                handleFavoriteToggle={handleFavoriteToggle}
                handleCategoryChange={handleCategoryChange}
                favorites={favorites}
                cartItems={cartItems}
                updateQuantity={updateQuantity}
                removeItem={removeItem}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
          </div>
        </div>
      </div>

      <Cart
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        onCheckout={() => {
          if (getTotalItems() === 0) {
            alert("Your cart is empty. Please add items before checkout.");
            return;
          }
          setShowCart(false);
          setShowCheckout(true);
        }}
      />

      <CheckoutModal
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        onConfirm={handleCheckout}
        restaurant={restaurantData}
      />

      {/* <FloatingCart
        totalItems={getTotalItems()}
        totalPrice={getTotalPrice()}
        onCartClick={() => setShowCart(true)}
      /> */}

      {/* Loading Overlay for Add to Cart */}
      {/* {addingToCart && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl flex items-center space-x-4 animate-pulse">
            <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
            <span className="text-lg font-semibold text-gray-900">
              Adding to cart...
            </span>
          </div>
        </div>
      )} */}
    </>
  );
};

export default MenuPage;
